import { useEffect, useState } from "react";
import TenantLicenseTable from "./TenentTable";
import { useTenantsUsedData } from "@/hooks/useAdminDashboardService";
import type { TenantItem } from "@/types/tenantService";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";

interface LicenseData {
  tenant: string;
  assigned: number;
  used: number;
  expiry: string;
  type: string;
  hasStripePlan?: boolean;
  createdAt?: string;
  accountType?: string;
  contactEmail: string;
  token: number;
  status?: string | boolean;
  id?: string;
}

const Index = () => {
  const { isDark } = useDashboardTheme();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [actualSearch, setActualSearch] = useState("");
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setActualSearch(searchQuery);
      setPage(1); // Reset page on new search
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const [allData, setAllData] = useState<LicenseData[]>([]);
  const limit = 10;
  const [totalItems, setTotalItems] = useState(0);

  const { data, isError, isLoading, isFetching, refetch } = useTenantsUsedData(
    page,
    limit,
    actualSearch
  );

  const totalPages = Math.ceil(totalItems / limit);

  useEffect(() => {
    if (data?.data?.items) {
      const transformed = data.data.items.filter((item: TenantItem) => item.type !== "INDIVIDUAL").map(
        (item: TenantItem): LicenseData => {
          const activeSub = item.subscriptions?.find((s: any) => s.status === "ACTIVE");
          const subscription = activeSub || item.subscriptions?.[0];
          const usage = item.TenantUsageCounter?.[0];
          const effectivePlan = activeSub?.planMaster || item.planMaster;
          // Only paid plans (STARTER, PRO, etc.) should have an active plan & expiry date
          const planName = (effectivePlan?.name || subscription?.plan || "").trim().toUpperCase();
          const hasPurchasedPlan = Boolean(
            (planName && planName !== "FREE" && (activeSub?.status === "ACTIVE" || subscription?.status === "ACTIVE")) ||
            subscription?.stripeSubscriptionId
          );

          return {
            tenant: item.name ?? "N/A",
            assigned: subscription?.licenseCount ?? effectivePlan?.licenselimit ?? 0,
            used: usage?.used ?? 0,
            expiry: hasPurchasedPlan ? (activeSub?.endDate ?? subscription?.endDate ?? "") : "",
            token: subscription?.tokenCount ?? effectivePlan?.tokenlimit ?? 0,
            type: effectivePlan?.displayName || effectivePlan?.name || (hasPurchasedPlan ? "Starter" : "Free"),
            contactEmail: item.contactEmail ?? "N/A",
            status: item?.status,
            id: item?.id,
            hasStripePlan: hasPurchasedPlan,
            createdAt: item?.createdAt ?? "",
            accountType: item?.type ?? "COMPANY",
          };
        },
      );
      setAllData(transformed);
      if (data?.data?.totalCount !== undefined) {
        setTotalItems(data.data.totalCount);
      }
    }
  }, [data, page]);

  if (isLoading && allData.length === 0)
    return <div className="text-white">Loading...</div>;

  if (isError)
    return <div className="text-red-500">Failed to load tenants</div>;

  return (
    <>
      <div className={"p-4 mt-20 " + (isDark ? "" : "light-theme-dashboard")}>
        <TenantLicenseTable
          data={allData}
          showHeading
          currentPage={page}
          isFetching={isFetching}
          totalPages={totalPages || 1}
          setCurrentPage={(newPage) => setPage(newPage)}
          setShouldFetch={refetch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </div>
    </>
  );
};

export default Index;
